import { useEffect, useState, useCallback } from 'react';
import { Clock, Factory, ChartBar } from '@phosphor-icons/react';
import { useT } from '@/i18n/LanguageContext';
import { mfgApi } from './mfgApi';
import { DataTable } from '@/components/ui/DataTable';

const effClass = (v) => (v == null ? 'text-zinc-300' : v >= 100 ? 'text-emerald-600' : v >= 80 ? 'text-amber-600' : 'text-rose-600');

export default function LaborReport() {
  const { t } = useT();
  const [data, setData] = useState({ items: [], stations: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    mfgApi.laborReport().then((d) => setData(d || { items: [], stations: [] })).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const hrs = (v) => (v != null ? `${v} ${t('mfg.bom.hoursUnit')}` : '—');
  const yen = (v) => (v != null ? `¥${Number(v).toLocaleString()}` : '—');

  return (
    <div className="space-y-6 page-enter" data-testid="mfg-labor-report">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{t('mfg.labor.title')}</h1>
        <p className="text-sm text-zinc-500 mt-0.5">{t('mfg.labor.desc')}</p>
      </div>

      {/* Per item */}
      <section>
        <h2 className="text-sm font-semibold text-zinc-700 mb-2 flex items-center gap-1.5"><Clock size={16} weight="duotone" className="text-indigo-500" /> {t('mfg.labor.byItem')}</h2>
        <DataTable
          dataTestId="labor-item-table"
          rows={data.items}
          rowKey={(r) => r.item_id}
          rowTestId={(r) => `labor-item-${r.item_id}`}
          defaultSort={{ key: 'efficiency_pct', dir: 'asc' }}
          empty={{ icon: <ChartBar size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: t('mfg.labor.empty'), testid: 'labor-item-empty' }}
          columns={[
            { key: 'item_name', primary: true, header: t('mfg.bomList.item'), sortValue: (r) => r.item_name || '',
              cell: (r) => (
                <div className="flex flex-col">
                  <span className="font-medium text-zinc-800">{r.item_name || '—'}</span>
                  {r.item_sku && <span className="font-mono text-[11px] text-zinc-400">{r.item_sku}</span>}
                </div>
              ) },
            { key: 'standard_hours', header: t('mfg.labor.standard'), align: 'right', sortValue: (r) => r.standard_hours ?? -1,
              cell: (r) => <span className="tabular-nums text-zinc-700">{hrs(r.standard_hours)}</span> },
            { key: 'avg_actual_hours', header: t('mfg.labor.actual'), align: 'right', sortValue: (r) => r.avg_actual_hours ?? -1,
              cell: (r) => <span className="tabular-nums text-indigo-600">{hrs(r.avg_actual_hours)}</span> },
            { key: 'efficiency_pct', header: t('mfg.labor.efficiency'), align: 'right', sortValue: (r) => r.efficiency_pct ?? 99999,
              cell: (r) => <span className={`tabular-nums font-semibold ${effClass(r.efficiency_pct)}`}>{r.efficiency_pct != null ? `${r.efficiency_pct}%` : '—'}</span> },
            { key: 'standard_cost', header: t('mfg.labor.stdCost'), align: 'right', sortValue: (r) => r.standard_cost ?? -1,
              cell: (r) => <span className="tabular-nums text-zinc-700">{yen(r.standard_cost)}</span> },
            { key: 'avg_actual_cost', header: t('mfg.labor.actualCost'), align: 'right', sortValue: (r) => r.avg_actual_cost ?? -1,
              cell: (r) => <span className="tabular-nums text-emerald-600">{yen(r.avg_actual_cost)}</span> },
            { key: 'cost_variance', header: t('mfg.labor.costVariance'), align: 'right', sortValue: (r) => r.cost_variance ?? 0,
              cell: (r) => (r.cost_variance == null ? <span className="text-zinc-300">—</span>
                : <span className={`tabular-nums font-medium ${r.cost_variance <= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{r.cost_variance > 0 ? '+' : ''}{yen(r.cost_variance)}</span>) },
            { key: 'sample_count', header: t('mfg.labor.samples'), align: 'right',
              cell: (r) => <span className="tabular-nums text-zinc-500">{r.sample_count}</span> },
          ]}
        />
      </section>

      {/* Per station */}
      <section>
        <h2 className="text-sm font-semibold text-zinc-700 mb-2 flex items-center gap-1.5"><Factory size={16} weight="duotone" className="text-orange-500" /> {t('mfg.labor.byStation')}</h2>
        <DataTable
          dataTestId="labor-station-table"
          rows={data.stations}
          rowKey={(r) => r.work_center_id}
          rowTestId={(r) => `labor-station-${r.work_center_id}`}
          defaultSort={{ key: 'work_center_name', dir: 'asc' }}
          empty={{ text: t('mfg.labor.empty'), testid: 'labor-station-empty' }}
          columns={[
            { key: 'work_center_name', primary: true, header: t('mfg.bomList.station'), sortValue: (r) => r.work_center_name || '',
              cell: (r) => <span className="font-medium text-zinc-800">{r.work_center_name || '—'}</span> },
            { key: 'avg_actual_hours', header: t('mfg.labor.avgActual'), align: 'right', sortValue: (r) => r.avg_actual_hours ?? -1,
              cell: (r) => <span className="tabular-nums text-indigo-600">{hrs(r.avg_actual_hours)}</span> },
            { key: 'hourly_rate', header: t('mfg.wc.hourlyRate'), align: 'right', sortValue: (r) => r.hourly_rate ?? -1,
              cell: (r) => <span className="tabular-nums text-zinc-500">{r.hourly_rate != null ? `¥${Number(r.hourly_rate).toLocaleString()}/h` : '—'}</span> },
            { key: 'avg_actual_cost', header: t('mfg.labor.actualCost'), align: 'right', sortValue: (r) => r.avg_actual_cost ?? -1,
              cell: (r) => <span className="tabular-nums text-emerald-600">{yen(r.avg_actual_cost)}</span> },
            { key: 'sample_count', header: t('mfg.labor.samples'), align: 'right',
              cell: (r) => <span className="tabular-nums text-zinc-500">{r.sample_count}</span> },
          ]}
        />
      </section>
    </div>
  );
}
